import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './auth';
import { ChatService } from '../api/services/chat.service';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadRooms, setUnreadRooms] = useState({});
  const currentOpenRoomRef = useRef(null);
  const eventSource = useRef(null);
  const reconnectTimeout = useRef(null);
  const navigate = useNavigate();
  const chatMessageCallbacks = useRef([]);

  const registerChatCallback = useCallback((callback) => {
    chatMessageCallbacks.current.push(callback);
    return () => {
      chatMessageCallbacks.current = chatMessageCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  useEffect(() => {
    if (Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  const fetchUnreadCounts = async () => {
    if (!user) return;
    try {
      const res = await ChatService.getRooms();
      let total = 0;
      const roomsMap = {};
      res.data.forEach(room => {
        roomsMap[room.id] = room.unread_count || 0;
        total += room.unread_count || 0;
      });
      setUnreadRooms(roomsMap);
      setUnreadChatCount(total);
    } catch (e) {
      console.error("Failed to fetch rooms unread count", e);
    }
  };

  const handleNtfyMessage = (data) => {
    if (!user) return;
    
    try {
      const title = data.title || data.x_title || 'Уведомление';
      const body = data.message || data.x_message || '';
      const link = data.x_link || data.click || null;
      const tags = data.tags || [];

      // ntfy 2.x не форвардит кастомные поля — roomId живёт в click-URL (/chat?roomId=N)
      const roomIdFromLink = (url) => {
        const m = String(url || '').match(/roomId=(\d+)/);
        return m ? parseInt(m[1], 10) : null;
      };
      const chatRoomId = roomIdFromLink(link);
      const isServiceEvent = title === 'ui_event';   // read_receipt и пр. — без popup

      if (chatRoomId && !isServiceEvent) {
        chatMessageCallbacks.current.forEach(cb => {
          try {
            // Тихий рефresh сообщений чата (chat_message в событии больше
            // нет — ntfy не форвардит кастомные поля, тянем из API)
            cb({ roomId: chatRoomId, refresh: true });
          } catch (cbErr) {
            console.error('Chat callback error:', cbErr);
          }
        });

        if (chatRoomId !== currentOpenRoomRef.current) {
          setUnreadRooms(prev => ({ ...prev, [chatRoomId]: (prev[chatRoomId] || 0) + 1 }));
          setUnreadChatCount(total => total + 1);
        }
      }

      if ((tags.includes('speech_balloon') || tags.includes('at_sign')) && !isServiceEvent) {
        notification.info({
          message: title,
          description: body,
          onClick: () => {
            if (link) navigate(link);
          },
        });

        if (Notification.permission === 'granted') {
          const osNotif = new Notification(title, { body });
          osNotif.onclick = () => {
            window.focus();
            if (link) navigate(link);
          };
        }
      }
    } catch (e) {
      console.error("Error parsing ntfy message", e);
    }
  };

  useEffect(() => {
    if (!user) {
      if (eventSource.current) {
        eventSource.current.close();
        eventSource.current = null;
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      setUnreadChatCount(0);
      setUnreadRooms({});
      chatMessageCallbacks.current = [];
      currentOpenRoomRef.current = null;
      return;
    }

    fetchUnreadCounts();

    const connectSSE = () => {
      if (eventSource.current) {
        eventSource.current.close();
      }

      const sseUrl = `/ntfy/terra-sup-user-${user.id}/sse`;
      eventSource.current = new EventSource(sseUrl);

      eventSource.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleNtfyMessage(data);
        } catch (e) {
          console.error("Error parsing SSE message", e);
        }
      };

      eventSource.current.onerror = () => {
        if (eventSource.current) {
          eventSource.current.close();
          eventSource.current = null;
        }
        reconnectTimeout.current = setTimeout(() => {
          if (user) {
            connectSSE();
          }
        }, 5000);
      };
    };

    connectSSE();

    return () => {
      if (eventSource.current) {
        eventSource.current.close();
        eventSource.current = null;
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };
  }, [user]);

  const markRoomAsRead = async (roomId) => {
    try {
      await ChatService.readRoom(roomId);
      setUnreadRooms(prev => {
        const roomCount = prev[roomId] || 0;
        if (roomCount > 0) {
          setUnreadChatCount(total => Math.max(0, total - roomCount));
        }
        return { ...prev, [roomId]: 0 };
      });
    } catch (e) {
      console.error("Failed to mark room as read", e);
    }
  };

  const setCurrentOpenRoom = useCallback((roomId) => {
    currentOpenRoomRef.current = roomId;
  }, []);

  return (
    <NotificationContext.Provider value={{ unreadChatCount, unreadRooms, markRoomAsRead, fetchUnreadCounts, registerChatCallback, setCurrentOpenRoom }}>
      {children}
    </NotificationContext.Provider>
  );
};
